import Box from '@mui/material/Box';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EmptyStateMessage } from '~components/Common/EmptyStateMessage';
import Tabs, { TabProps } from '~components/Tabs/Tabs';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import DeletedJobsTab from '~pages/Settings/DeletedRecords/components/DeletedJobsTab';
import DeletedOrdersTab from '~pages/Settings/DeletedRecords/components/DeletedOrdersTab';
import DeletedProjectsTab from '~pages/Settings/DeletedRecords/components/DeletedProjectsTab';
import DeletedTicketsTab from '~pages/Settings/DeletedRecords/components/DeletedTicketsTab';
import {
  DELETED_RECORDS_TAB_URL_PARAM,
  DeletedRecordsTab,
} from '~pages/Settings/DeletedRecords/constants';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const DeletedRecords = observer(function DeletedRecords() {
  const { userStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();

  const companyId = getEffectiveUserCompanyId(userStore);
  const { canDeleteJob, canDeleteOrder, canDeleteProject, canDeleteTicket } =
    userStore.getPermissions();

  const permittedTabs = useMemo(() => {
    const tabs: TabProps<DeletedRecordsTab>[] = [];

    if (canDeleteProject) {
      tabs.push({
        label: t('settings.deleted_records.tabs.projects'),
        value: DeletedRecordsTab.PROJECTS,
      });
    }

    if (canDeleteOrder) {
      tabs.push({
        label: t('settings.deleted_records.tabs.orders'),
        value: DeletedRecordsTab.ORDERS,
      });
    }

    if (canDeleteJob) {
      tabs.push({
        label: t('settings.deleted_records.tabs.jobs'),
        value: DeletedRecordsTab.JOBS,
      });
    }

    if (canDeleteTicket) {
      tabs.push({
        label: t('settings.deleted_records.tabs.tickets'),
        value: DeletedRecordsTab.TICKETS,
      });
    }

    return tabs;
  }, [canDeleteJob, canDeleteOrder, canDeleteProject, canDeleteTicket]);

  // The API serves discarded records to anyone who can list live ones, so a hand-typed
  // ?tab= has to be clamped rather than defaulted.
  const requestedTab = searchParams.get(DELETED_RECORDS_TAB_URL_PARAM);
  const selectedTab =
    permittedTabs.find((tab) => tab.value === requestedTab)?.value ??
    permittedTabs[0]?.value;

  const handleTabChange = useCallback(
    (value: DeletedRecordsTab) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.set(DELETED_RECORDS_TAB_URL_PARAM, value);

        return next;
      });
    },
    [setSearchParams],
  );

  return (
    <>
      <SettingsPageHeader
        title={t('settings.deleted_records.title')}
        description={t('settings.deleted_records.description')}
      />

      {selectedTab === undefined ? (
        <EmptyStateMessage message={`${t('settings.deleted_records.no_access')}`} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Tabs
            selected={selectedTab}
            tabs={permittedTabs}
            onChange={handleTabChange}
            sx={{ maxWidth: '480px' }}
          />

          {selectedTab === DeletedRecordsTab.PROJECTS && (
            <DeletedProjectsTab companyId={companyId} />
          )}
          {selectedTab === DeletedRecordsTab.ORDERS && (
            <DeletedOrdersTab companyId={companyId} />
          )}
          {selectedTab === DeletedRecordsTab.JOBS && (
            <DeletedJobsTab companyId={companyId} />
          )}
          {selectedTab === DeletedRecordsTab.TICKETS && (
            <DeletedTicketsTab companyId={companyId} />
          )}
        </Box>
      )}
    </>
  );
});

export default DeletedRecords;
